import { nextTick } from 'vue';
import { shouldKeepScroll, writeScrollPosition } from '~/utils/scrollRestoration';

const HISTORY_NAV_KEY = 'scroll-history-navigation';

const CARD_ANCHOR_SELECTOR = '.client-card a[href]';

const getAnchorIdFromHref = (href: string | null): string | null => {
  if (!href) return null;
  const clean = href.split('#')[0]?.split('?')[0] ?? href;
  const segments = clean.split('/').filter(Boolean);
  const last = segments[segments.length - 1];
  if (!last) return null;
  return last;
};

const getAnchorSnapshot = () => {
  const anchors = Array.from(document.querySelectorAll<HTMLAnchorElement>(CARD_ANCHOR_SELECTOR));
  let firstBelow: { id: string; offset: number } | null = null;
  let lastAbove: { id: string; offset: number } | null = null;

  for (const anchor of anchors) {
    const id = getAnchorIdFromHref(anchor.getAttribute('href'));
    if (!id) continue;
    const card = anchor.closest('.client-card') ?? anchor;
    const rect = card.getBoundingClientRect();
    if (rect.bottom <= 0 || rect.top >= window.innerHeight) continue;

    if (rect.top >= 0) {
      if (!firstBelow || rect.top < firstBelow.offset) {
        firstBelow = { id, offset: rect.top };
      }
    } else if (!lastAbove || rect.top > lastAbove.offset) {
      lastAbove = { id, offset: rect.top };
    }
  }

  return firstBelow ?? lastAbove;
};

export default defineNuxtPlugin((nuxtApp) => {
  const router = useRouter();
  const historyNavigation = useState<boolean>(HISTORY_NAV_KEY, () => false);

  if (process.client && 'scrollRestoration' in window.history) {
    window.history.scrollRestoration = 'manual';
  }

  const markHistoryNavigation = () => {
    historyNavigation.value = true;
  };

  window.addEventListener('popstate', markHistoryNavigation, { capture: true });

  const savePosition = (route: typeof router.currentRoute.value) => {
    if (!route.fullPath) return;
    if (!shouldKeepScroll(route)) return;

    const anchor = getAnchorSnapshot();
    const scrollHeight = Math.max(
      document.documentElement?.scrollHeight ?? 0,
      document.body?.scrollHeight ?? 0
    );

    writeScrollPosition(route, {
      left: window.scrollX || 0,
      top: window.scrollY || 0,
      anchorId: anchor?.id,
      anchorOffset: anchor?.offset,
      scrollHeight,
    });
  };

  const scrollToTopAfterFinish = () => {
    nuxtApp.hooks.hookOnce('page:finish', async () => {
      await nextTick();
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    });
  };

  router.beforeEach((_to, from) => {
    savePosition(from);
  });

  router.afterEach((to) => {
    if (to.hash) {
      historyNavigation.value = false;
      return;
    }

    if (historyNavigation.value) {
      historyNavigation.value = false;
      return;
    }

    scrollToTopAfterFinish();
  });

  const saveCurrent = () => savePosition(router.currentRoute.value);
  window.addEventListener('pagehide', saveCurrent, { capture: true });
  window.addEventListener('beforeunload', saveCurrent, { capture: true });

  nuxtApp.hooks.hook('app:beforeUnmount', () => {
    window.removeEventListener('popstate', markHistoryNavigation, { capture: true });
    window.removeEventListener('pagehide', saveCurrent, { capture: true });
    window.removeEventListener('beforeunload', saveCurrent, { capture: true });
  });
});
